import React from 'react';

import { ExpandMoreButton } from '@components/ExpandMoreButton';
import { ErrorMessage } from '@components/Forms/ErrorMessage';
import {
  FileUpload as OldFileUpload,
  FileUploadProps as OldFileUploadProps,
} from '@components/Forms/FileUpload';
import { FormItem as OldFormItem } from '@components/Forms/FormItem';
import { SingleUploadButton as OldSingleUploadButton } from '@components/Forms/SingleUploadButton';
import { TextInput as OldTextInput, TextInputProps } from '@components/Forms/TextInput';
import { TextBox, TextBoxProps } from '@components/TextBox';
import { makeMuiStyles, useCommonStyles, ClassMap } from '@styles';
import { Field, formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { useField } from 'formik';

const strings = createStrings({
  ja: {
    edit: '編集',
    cancel: 'キャンセル',
    promptCancel: '変更は保存されていませんが、本当にキャンセルしますか？',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  input: {
    ...common.middleText,
    width: 165,
  },
  simpleInput: {
    ...common.middleText,
  },
  textInput: {
    padding: 2,
    paddingLeft: 6,
    paddingRight: 6,
    borderRadius: 3,
    minHeight: 32,
  },
  label: {
    ...common.middleText,
    ...common.textGray700,
    width: 85,
    textAlign: 'left',
    paddingTop: 5,
  },
  formTitle: {
    ...common.flexInline,
    ...common.normalText,
    ...common.fontWeight600,
    alignItems: 'center',
    color: theme.palette.text.primary,
    marginBottom: '1rem',
  },
  formTitleDecoration: {
    height: 21,
    width: 5,
    backgroundColor: theme.palette.primary.main,
    marginRight: 7,
  },
  formTitleEdit: {
    ...common.middleText,
    ...common.baseButton,
    color: theme.palette.primary.main,
    fontWeight: 400,
    marginLeft: '1rem',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  formTitleExpand: {
    marginLeft: 'auto',
  },
  textDisplayContainer: {
    ...common.flexInline,
    ...common.middleText,
    alignItems: 'flex-start',
    padding: '0.5rem',
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  textDisplayItem: {
    ...common.flexInline,
    width: 240,
    alignItems: 'center',
    '&$textDisplayItemValue': {
      flex: 1,
      whiteSpace: 'pre-wrap',
    },
  },
  textDisplayItemValue: {
    color: theme.palette.text.secondary,
    fontWeight: 400,
  },
  simpleTextInputContainer: {
    ...common.flexInline,
    ...common.itemsStart,
    ...common.middleText,
    ...common.textGray700,
    columnGap: '0.5rem',
  },
  simpleTextInputLabel: {
    ...common.middleText,
    ...common.textGray700,
    paddingTop: 5,
  },
}));

export const TextInput: typeof OldTextInput = (props) => {
  const { formClasses, classes: textClasses, ...rest } = props;

  const classes = useStyles();

  return (
    <OldTextInput
      formClasses={{
        ...formClasses,
        label: clsx(classes.label, formClasses?.label),
      }}
      classes={{
        ...textClasses,
        input: clsx(classes.input, textClasses?.input),
        textInput: clsx(classes.textInput, textClasses?.textInput),
      }}
      {...rest}
    />
  );
};

export const FormItem: typeof OldFormItem = (props) => {
  const { classes: customsClasses } = props;
  const classes = useStyles();

  return (
    <OldFormItem
      {...props}
      classes={{
        label: clsx(classes.label, customsClasses?.label),
      }}
    />
  );
};

interface FileUploadProps extends OldFileUploadProps {
  label: string;
}

export const FileUpload: React.FC<FileUploadProps> = (props) => {
  const { name, label } = props;

  return (
    <FormItem name={name} label={<label htmlFor={name}>{label}</label>}>
      <OldFileUpload {...props} />
    </FormItem>
  );
};

export const SingleUploadButton: typeof OldSingleUploadButton = (props) => {
  const { formClasses, ...rest } = props;

  const classes = useStyles();

  return (
    <OldSingleUploadButton
      formClasses={{
        label: classes.label,
        ...formClasses,
      }}
      {...rest}
    />
  );
};

interface FormTitleProps {
  title: React.ReactNode;
  noEdit?: boolean;
  editDisabled?: boolean;
  editDisabledReason?: string;
  editting?: boolean;
  onChangeEditting?: (editting: boolean) => void;
  noExpand?: boolean;
  expanded?: boolean;
  onChangeExpanded?: (expanded: boolean) => void;
  noCreate?: boolean;
  onCreate?: () => void;
}

export const FormTitle: React.FC<FormTitleProps> = (props) => {
  const {
    title,
    noEdit = false,
    editDisabled = false,
    editDisabledReason = undefined,
    editting,
    onChangeEditting,
    noExpand = true,
    expanded,
    onChangeExpanded,
    noCreate = true,
    onCreate,
  } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div
      className={classes.formTitle}
      {...(!noExpand && {
        style: { cursor: 'pointer' },
        onClick: (e) => {
          // Ignore clicking inside clickable items
          if (
            !(e.target instanceof HTMLButtonElement) &&
            !(e.target instanceof HTMLAnchorElement)
          ) {
            onChangeExpanded?.(!expanded);
          }
        },
      })}
    >
      <div className={classes.formTitleDecoration} data-testid="blockTitle" />
      {title}
      {!noEdit && (
        <a
          title={editDisabledReason}
          className={clsx(classes.formTitleEdit, editDisabled && classes.linkDisabled)}
          onClick={() => {
            if (!editDisabled) {
              if (editting === true && !confirm(strings.promptCancel)) {
                return;
              }
              onChangeEditting?.(!editting);
            }
          }}
        >
          {editting ? strings.cancel : strings.edit}
        </a>
      )}
      {!noCreate && (
        <a
          className={classes.formTitleEdit}
          onClick={() => {
            onCreate?.();
          }}
        >
          {formStrings.addNew}
        </a>
      )}
      {!noExpand && (
        <div className={classes.formTitleExpand}>
          <ExpandMoreButton
            expanded={expanded}
            color="primary"
            onClick={() => {
              onChangeExpanded?.(!expanded);
            }}
          />
        </div>
      )}
    </div>
  );
};

export interface DetailTextDisplayProps {
  firstNameField?: Field<string>;
  firstName?: string;
  lastNameField?: Field<string>;
  lastName?: string;
  field?: Field<any>;
  textInputProps?: Partial<TextInputProps>;
  value: any;
  classes?: ClassMap<'container'>;
  label?: React.ReactNode;
}

export const DetailTextDisplay: React.FC<DetailTextDisplayProps> = (props) => {
  const { field, value, classes: customClasses = {}, label = field?.label ?? '' } = props;

  const classes = useStyles();

  return (
    <div className={clsx(classes.textDisplayContainer, customClasses.container)}>
      <div className={classes.textDisplayItem}>{label}</div>
      <div className={clsx(classes.textDisplayItem, classes.textDisplayItemValue)}>{value}</div>
    </div>
  );
};

export interface SimpleTextInputProps extends Omit<TextBoxProps, 'onChange' | 'label'> {
  label?: React.ReactNode;
  name: string;
  labelProps?: SimpleLabelProps;
  textInputWidth?: number;
  textInputElement?: React.ReactNode;
  align?: 'center';
  hideError?: boolean;
  className?: string;
  textInputFullWidth?: boolean; // Let the inner div be width: 100%
  containerStyle?: React.CSSProperties;
}
export const SimpleTextInput: React.FC<SimpleTextInputProps> = (props) => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const { choose, multiChoose } = props;
  const defaultTextInputWidth = choose || multiChoose ? 140 : 165;
  const {
    label,
    name,
    labelProps,
    textInputWidth = defaultTextInputWidth,
    textInputElement,
    align,
    onBlur,
    hideError = false,
    className,
    classes: textClasses,
    textInputFullWidth,
    containerStyle,
    ...rest
  } = props;

  const [field, meta, helpers] = useField<string | boolean | number>(name);

  const hasError = !!(meta.touched && typeof meta.error === 'string');

  const finalField: typeof field = {
    ...field,
    onBlur: (e: any) => {
      onBlur?.(e);
      field.onBlur(e);
    },
  };

  return (
    <div
      style={containerStyle}
      className={clsx(
        classes.simpleTextInputContainer,
        className,
        align === 'center' && commonClasses.justifyCenter,
      )}
    >
      {label && <SimpleLabel {...labelProps}>{label}</SimpleLabel>}

      <div style={textInputFullWidth ? { flex: 1 } : { width: 'min-content' }}>
        {textInputElement ? (
          textInputElement
        ) : (
          <TextBox
            {...finalField}
            classes={{
              ...textClasses,
              input: clsx(classes.simpleInput, textClasses?.input),
              textInput: clsx(classes.textInput, textClasses?.textInput),
            }}
            style={{
              width: textInputFullWidth ? undefined : textInputWidth,
            }}
            {...(rest as any)}
            hasError={hasError}
            setValue={(v) => helpers.setValue(v)}
            setTouched={() => helpers.setTouched(true)}
          />
        )}
        {!hideError && <ErrorMessage name={name} />}
      </div>
    </div>
  );
};

export interface SimpleLabelProps {
  textAlign?: 'center' | 'left' | 'right';
  width?: number | string;
  className?: string;
}

export const SimpleLabel: React.FC<React.PropsWithChildren<SimpleLabelProps>> = (props) => {
  const classes = useStyles();

  const { width = 80, textAlign = 'right', children, className } = props;

  return (
    <div style={{ width, textAlign }} className={clsx(classes.simpleTextInputLabel, className)}>
      {children}
    </div>
  );
};

interface MultiSelectInputProps<T> extends Omit<TextBoxProps, 'onChange' | 'label' | 'options'> {
  name: string;
  label?: React.ReactNode;
  textInputFullWidth?: boolean;
  labelProps?: SimpleLabelProps;
  align?: 'center';
  className?: string;
  options?: { label: string; value: T }[];
  containerStyle?: React.CSSProperties;
  hideError?: boolean;
  textInputWidth?: number;
}

export const MultiSelectInput = <T extends any>(props: MultiSelectInputProps<T>) => {
  const {
    name,
    label,
    labelProps,
    options,
    align,
    className,
    containerStyle,
    textInputFullWidth,
    classes: textClasses,
    textInputWidth = 139,
    hideError = false,
    onBlur,
    ...rest
  } = props;
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [field, meta, helpers] = useField<T[]>(name);

  const hasError = !!(meta.touched && typeof meta.error === 'string');

  const finalField: typeof field = {
    ...field,
    onBlur: (e: any) => {
      onBlur?.(e);
      field.onBlur(e);
    },
  };

  const onClick = (value: T) => {
    helpers.setTouched(true);
    const checked = field.value?.includes(value);
    if (checked) {
      helpers.setValue(field.value.filter((v) => v !== value));
    } else {
      helpers.setValue([...(field.value ?? []), value]);
    }
  };

  return (
    <div
      style={containerStyle}
      className={clsx(
        classes.simpleTextInputContainer,
        className,
        align === 'center' && classes.justifyCenter,
      )}
    >
      {label && <SimpleLabel {...labelProps}>{label}</SimpleLabel>}

      <div style={textInputFullWidth ? { flex: 1 } : { width: 'min-content' }}>
        <TextBox
          {...finalField}
          multiChoose
          classes={{
            ...textClasses,
            input: clsx(classes.simpleInput, textClasses?.input),
            textInput: clsx(classes.textInput, textClasses?.textInput),
          }}
          style={{
            width: textInputFullWidth ? undefined : textInputWidth,
          }}
          {...(rest as any)}
          hasError={hasError}
          setValue={onClick}
          setTouched={() => helpers.setTouched(true)}
          options={options}
        />
        {!hideError && <ErrorMessage name={name} />}
      </div>
    </div>
  );
};
